import React, {useState, useEffect, useRef} from 'react';
import {Autocomplete, TextField, FormControl} from '@mui/material';
import {useInfiniteQuery} from '@tanstack/react-query';
import {getBilltos} from "../services/IsfaService";

const getPlaceholderLabel = (label) => (label ? label.replace(/ \*$/, "") : "");

const BilltoSearch = ({label, businessUnitCode, editable, required, initialValue = '', onChange}) => {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState(initialValue);
    const [options, setOptions] = useState([]);
    const debounceTimeout = useRef(null);

    useEffect(() => {
        if (initialValue !== '') {
            setInputValue(initialValue);
        }
    }, [initialValue]);

    // Add effect to respond to businessUnitCode changes
    // useEffect(() => {
    //     if (businessUnitCode) {
    //         // Simulate opening the dropdown to trigger data fetch
    //         setOpen(true);
    //         // Then close it after a short delay
    //         setTimeout(() => {
    //             setOpen(false);
    //         }, 100);
    //     }
    // }, [businessUnitCode]);

    const queryEnabled = !!businessUnitCode && (open || inputValue !== '');

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ['billtoItems', businessUnitCode, inputValue],
        queryFn: ({pageParam}) => getBilltos(pageParam, 10, {businessUnitCode, searchString: inputValue}),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.data.length ? allPages.length : undefined;
        },
        enabled: queryEnabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
    });

    useEffect(() => {
        if (data) {
            const fetchedOptions = data.pages.flatMap(page => page.data);
            setOptions(fetchedOptions);

            // search option where billtoId matches inputValue
            const found = fetchedOptions.find(option => option.billtoId === inputValue);
            if (found) {
                setValue(found);
                onChange(found);
            } else {
                // If no match found, reset value
                if (inputValue !== '') {
                    setValue(null);
                    setInputValue('');
                    onChange(null);
                }
            }
        }
    }, [data]);

    const handleScroll = (event) => {
        const bottom = event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;
        if (bottom && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleInputChange = (event, newInputValue) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            const substringBeforeSpace = newInputValue.split(' ')[0];
            setInputValue(substringBeforeSpace);
        }, 500);
    };

    return (
        <FormControl fullWidth size="small" margin="dense" required={required}>
            <Autocomplete
                open={open}
                onOpen={() => setOpen(true)}
                onClose={handleClose}
                onChange={(event, newValue) => {
                    setValue(newValue);
                    setInputValue(newValue ? newValue.billtoId : '');
                    onChange(newValue);
                }}
                getOptionLabel={(option) => {
                    // billtoId (retailLocationCount Retail Locations)
                    let label = option.billtoId;
                    if (option.retailLocationCount !== undefined) {
                        label += ` (${option.retailLocationCount} Retail Locations)`;
                    }
                    return label;
                }}
                renderOption={(props, option) => (
                    <li {...props} key={option.billtoId}>
                        {option.billtoId}
                        {option.retailLocationCount !== undefined && ` (${option.retailLocationCount} Retail Locations)`}
                    </li>
                )}
                options={options}
                loading={isFetching}
                onInputChange={handleInputChange}
                disabled={!editable}
                ListboxProps={{onScroll: handleScroll, sx: {maxHeight: 200}}}
                isOptionEqualToValue={(option, value) => option.billtoId === value.billtoId}
                value={value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        size="small"
                        inputProps={{...params.inputProps, placeholder: `Select ${getPlaceholderLabel(label)}`}}
                        sx={{'& input::placeholder': {color: 'rgba(0, 0, 0, 0.38)', opacity: 1}}}
                    />
                )}
            />
        </FormControl>
    );
};

export default BilltoSearch;
